import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("primergy_saved_email") || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => {
    try { return !!localStorage.getItem("primergy_saved_email"); } catch { return false; }
  });
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (!loading && user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email, password);
      try {
        if (remember) localStorage.setItem("primergy_saved_email", email);
        else localStorage.removeItem("primergy_saved_email");
      } catch {}
      toast.success("Login berhasil");
      nav("/");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left / brand panel */}
      <div className="hidden lg:flex relative flex-col justify-between bg-slate-950 text-white p-10 overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{
          background: "radial-gradient(1200px 500px at 10% 0%, #0ea5e955, transparent 60%), radial-gradient(900px 500px at 90% 100%, #ef444422, transparent 60%)"
        }}/>
        <div className="relative z-10 flex items-center gap-4">
          <img src="/primergy-logo.jpeg" alt="Primergy" className="w-14 h-14 rounded-full bg-white object-contain shadow-lg" />
          <div>
            <div className="font-heading font-bold text-lg tracking-tight">PT. PRIMERGY SOLUTION</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300">Plant Management System</div>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <h2 className="font-heading text-4xl xl:text-5xl font-black leading-[1.05] tracking-tight">
            One Team, <span className="text-sky-400">One Energy,</span> Better <span className="text-sky-400">Future.</span>
          </h2>
          <p className="text-slate-300 text-sm max-w-md leading-relaxed">
            Kolaborasi kita hari ini adalah bahan bakar untuk masa depan yang lebih cerah. Melalui sistem terintegrasi ini, mari perkuat sinergi tim PT Primergy Solution demi pengelolaan pabrik yang prima dan berkelanjutan.
          </p>

          <div className="grid grid-cols-3 gap-3 max-w-md pt-4">
            {[
              { k: "Modul aktif", v: "9" },
              { k: "Foto per txn", v: "≥ 4" },
              { k: "Role", v: "8" },
            ].map((s) => (
              <div key={s.k} className="rounded-lg bg-white/5 border border-white/10 p-3">
                <div className="font-heading font-bold text-2xl">{s.v}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{s.k}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[11px] text-slate-500 uppercase tracking-widest">
          © {new Date().getFullYear()} Primergy Solution · Internal Use Only
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form" autoComplete="on">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-600 mb-2">Login Operator</div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/primergy-logo.jpeg" alt="Primergy" className="w-14 h-14 rounded-full lg:hidden shadow-md" />
              <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Selamat datang kembali.</h1>
                <p className="text-sm text-slate-500 mt-1.5">Silakan masuk ke sistem manajemen pabrik Anda.</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">Email</label>
              <Input
                data-testid="login-email"
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="anda@primergy.com"
                className="mt-1.5 h-11"
                required
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">Password</label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
                  data-testid="login-toggle-password"
                >
                  {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 select-none cursor-pointer">
              <input
                type="checkbox"
                data-testid="login-remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
              />
              <span className="text-[12px] text-slate-600">Ingat email saya di perangkat ini</span>
            </label>
          </div>

          {err && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="login-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md transition-transform duration-200 hover:-translate-y-[1px]"
          >
            {submitting ? "Memproses…" : "Masuk"}
          </Button>

          <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-4">
            Belum punya akun? Silakan hubungi administrator sistem untuk pembuatan akun operator.
          </div>
        </form>
        <Toaster richColors position="top-right" />
      </div>
    </div>
  );
}
